import React, { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * 90-day Founding VIP Membership Drive countdown.
 *
 * - End date is configurable via `REACT_APP_FOUNDING_DRIVE_END` (ISO string).
 * - Falls back to a hardcoded launch (90 days from Jun 22, 2026).
 * - Ticks every second (lightweight setInterval).
 * - Persists the end-date to localStorage on first paint so it stays stable
 *   for returning visitors (no skew if the env changes between deploys).
 * - When the drive has ended, renders a "drive closed" badge instead.
 * - Fully i18n: labels + headlines pull from useI18n().
 */

const STORAGE_KEY = "adn_founding_drive_end_v2";  // bumped from v1 to invalidate old cached dates

// Default: 5 months AFTER July 4, 2026 (America's 250-year celebration) → Dec 4, 2026 23:59:59 UTC.
// Gives the pre-launch drive a clear runway tied to the 250-year story.
const DEFAULT_END_ISO = "2026-12-04T23:59:59Z";

function getDriveEnd() {
  // Priority: build-time env var → persisted value → default
  const fromEnv = process.env.REACT_APP_FOUNDING_DRIVE_END;
  if (fromEnv) return new Date(fromEnv);
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return new Date(stored);
    localStorage.setItem(STORAGE_KEY, DEFAULT_END_ISO);
  } catch { /* ignore */ }
  return new Date(DEFAULT_END_ISO);
}

function diff(now, end) {
  let ms = Math.max(0, end - now);
  const days = Math.floor(ms / 86_400_000); ms -= days * 86_400_000;
  const hours = Math.floor(ms / 3_600_000); ms -= hours * 3_600_000;
  const minutes = Math.floor(ms / 60_000); ms -= minutes * 60_000;
  const seconds = Math.floor(ms / 1_000);
  return { days, hours, minutes, seconds, total: end - now };
}

function pad(n, w = 2) {
  return String(n).padStart(w, "0");
}

function TimeBlock({ value, label, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center rounded-sm bg-[#0A1128] border border-[#D4AF37]/40 px-3 py-3 min-w-[68px] sm:min-w-[84px] shadow-[inset_0_-2px_0_rgba(212,175,55,0.5)]"
    >
      <div
        className="font-heading text-[28px] sm:text-[36px] leading-none font-bold text-[#D4AF37] tabular-nums"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </div>
      <div className="mt-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">
        {label}
      </div>
    </div>
  );
}

export default function FoundingDriveCountdown() {
  const { t } = useI18n();
  const end = useMemo(() => getDriveEnd(), []);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { days, hours, minutes, seconds, total } = diff(now, end);
  const expired = total <= 0;

  return (
    <div
      data-testid="founding-drive-countdown"
      className="relative w-full bg-gradient-to-r from-[#0A1128] via-[#0F162A] to-[#0A1128] border-y border-[#D4AF37]/30"
    >
      <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
          {/* Left — eyebrow + headline */}
          <div className="text-center sm:text-left">
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em] text-[#D4AF37]">
              <Clock className="w-3.5 h-3.5" />
              {expired ? t("countdown.eyebrow_expired") : t("countdown.eyebrow")}
            </div>
            <div className="mt-1 font-heading text-base sm:text-lg text-white leading-tight">
              {expired ? (
                t("countdown.tagline.expired")
              ) : (
                <>
                  {t("countdown.tagline.before")}{" "}
                  <span className="text-[#D4AF37]">{t("countdown.tagline.rate")}</span>{" "}
                  {t("countdown.tagline.after")}
                </>
              )}
            </div>
          </div>

          {/* Right — timer blocks */}
          {expired ? (
            <div
              data-testid="countdown-expired-badge"
              className="px-4 py-2 rounded-sm bg-[#9B1B30] text-white text-xs font-bold uppercase tracking-widest border-b-2 border-[#D4AF37]"
            >
              {t("countdown.expired_badge")}
            </div>
          ) : (
            <div className="flex flex-col items-end gap-2" data-testid="countdown-blocks-wrap">
              <div className="flex items-center gap-2 sm:gap-3" data-testid="countdown-blocks">
                <TimeBlock value={pad(days, 2)} label={t("countdown.label.days")} testid="cd-days" />
                <span className="text-[#D4AF37]/60 font-heading text-2xl sm:text-3xl">:</span>
                <TimeBlock value={pad(hours)} label={t("countdown.label.hours")} testid="cd-hours" />
                <span className="text-[#D4AF37]/60 font-heading text-2xl sm:text-3xl">:</span>
                <TimeBlock value={pad(minutes)} label={t("countdown.label.mins")} testid="cd-mins" />
                <span className="text-[#D4AF37]/60 font-heading text-2xl sm:text-3xl">:</span>
                <TimeBlock value={pad(seconds)} label={t("countdown.label.secs")} testid="cd-secs" />
              </div>
              {/* Human-readable end date — helps the user commit to the timeline instead
                  of just seeing days/hours ticking. Format: "Ends December 4, 2026". */}
              <div
                data-testid="countdown-end-date"
                className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold"
              >
                Drive ends {end.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}
              </div>
            </div>
          )}
        </div>

        {/* Progress hairline — visual % of drive elapsed */}
        {!expired && <DriveProgressBar end={end} t={t} />}
      </div>
    </div>
  );
}

function DriveProgressBar({ end, t }) {
  // 5-month drive (from July 4 America 250 to Dec 4, 2026). We compute start
  // as end - 5 months so the bar reflects the canonical drive length no
  // matter when the visitor arrives.
  const FIVE_MONTHS_MS = 153 * 86_400_000;  // ~5 months
  const start = new Date(end.getTime() - FIVE_MONTHS_MS);
  const now = Date.now();
  const elapsed = Math.max(0, Math.min(1, (now - start.getTime()) / FIVE_MONTHS_MS));
  const pct = Math.round(elapsed * 100);
  return (
    <div className="mt-4 w-full" data-testid="drive-progress">
      <div className="h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#9B1B30] via-[#D4AF37] to-[#9B1B30] transition-[width] duration-1000 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/75">
        <span>{t("countdown.progress.launched")}</span>
        <span data-testid="drive-progress-pct">{pct}{t("countdown.progress.complete")}</span>
        <span>{t("countdown.progress.ends")}</span>
      </div>
    </div>
  );
}
